import {ChangeDetectionStrategy, Component, computed, inject, input, signal} from '@angular/core';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {of} from 'rxjs';
import {catchError, debounceTime, switchMap} from 'rxjs/operators';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {AppointmentClash, AppointmentClashType, pickerToWallClock} from '@xmedical/core';
import dayjs from 'dayjs/esm';
import {CALENDAR_DATA_PROVIDER} from '../../tokens/calendar.tokens';

/**
 * Non-blocking warning shown on the appointment forms: queries the clash endpoint
 * (debounced) whenever the location / time changes and lists any existing appointments
 * that overlap this practitioner or location. Purely advisory — it never prevents save.
 *
 * Practitioner is fixed by the parent (the appointment's practitioner, or the booking's
 * default); this component only reads it. When the provider can't fetch clashes (e.g. the
 * organisation-portal) or the inputs aren't complete, it renders nothing.
 */
@Component({
  selector: 'app-appointment-clash-banner',
  templateUrl: './appointment-clash-banner.component.html',
  styleUrls: ['./appointment-clash-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatButtonModule],
})
export class AppointmentClashBannerComponent {
  locationId = input<number | null>(null);
  practitionerId = input<number | null>(null);
  start = input<Date | string | null>(null);
  end = input<Date | string | null>(null);
  /** The appointment being edited, so it doesn't clash with itself. Omit on create. */
  excludeAppointmentId = input<number | null>(null);

  private dataProvider = inject(CALENDAR_DATA_PROVIDER);

  readonly clashes = signal<AppointmentClash[]>([]);
  readonly expanded = signal(false);

  private readonly request = computed(() => ({
    locationId: this.locationId(),
    practitionerId: this.practitionerId(),
    start: this.start(),
    end: this.end(),
    exclude: this.excludeAppointmentId(),
  }));

  constructor() {
    toObservable(this.request).pipe(
      debounceTime(400),
      switchMap(req => {
        const provider = this.dataProvider;
        // Need a fetch capability, a concrete location + practitioner, and a time range.
        if (!provider.findClashes || req.locationId == null || req.practitionerId == null || !req.start || !req.end) {
          return of<AppointmentClash[]>([]);
        }
        const start = pickerToWallClock(req.start) as unknown as string;
        const end = pickerToWallClock(req.end) as unknown as string;
        return provider.findClashes(req.locationId, req.practitionerId, start, end, req.exclude ?? undefined).pipe(
          // Advisory only — a failed clash check must never disrupt the form.
          catchError(() => of<AppointmentClash[]>([])),
        );
      }),
      takeUntilDestroyed(),
    ).subscribe(clashes => {
      this.clashes.set(clashes);
      if (clashes.length === 0) {
        this.expanded.set(false);
      }
    });
  }

  get hasClashes(): boolean {
    return this.clashes().length > 0;
  }

  toggle(): void {
    this.expanded.update(open => !open);
  }

  clashTypeLabel(type: AppointmentClashType): string {
    return type === AppointmentClashType.Location ? 'Location' : 'Practitioner';
  }

  /** Render a zone-less wall-clock clash as "Thu 25 Jun, 09:30 – 10:00". */
  clashTime(clash: AppointmentClash): string {
    const start = dayjs(clash.start);
    const day = start.format('ddd D MMM, HH:mm');
    return clash.end ? `${day} – ${dayjs(clash.end).format('HH:mm')}` : day;
  }

  clashWho(clash: AppointmentClash): string {
    return [clash.patientName, clash.practitionerName, clash.locationName].filter(Boolean).join(' · ');
  }
}
